import React, { useEffect, Fragment } from "react"
import PropTypes from "prop-types"
import DeleteSite from "./DeleteSite"
import CmsIntegrations from "@modules/site/settings/components/CmsIntegrations"
import { PageWithTabsContentSection } from "@modules/ui/layouts/Containers"
import { useTracker } from "@modules/analytics"
import {
  SidebarColumn,
  Content,
  InnerLayout,
} from "@modules/ui/layouts/ColumnLayout"
import { useSiteSettingsNavigation } from "./Settings.helpers"
import { SiteSettingsSection } from "../constants"
import GeneralSettingsSection from "./GeneralSection"
import ReportsSection from "./ReportsSection"
import { isProduction } from "../../../env/constants"
import ExperimentalFeaturesSection from "./ExperimentalFeaturesSection"
import BuildsSection from "./BuildsSection"
import { IntegrationTargetSection } from "./IntegrationTargetSection"
import { useSitePermissionsFieldsFragment } from "@modules/site/shared/fragments.generated"
import { useFlags } from "@modules/featureFlags"
import { PreviewSettingsSection } from "./PreviewSettingsSection"

const propTypes = {
  siteId: PropTypes.string.isRequired,
  buildsEnabled: PropTypes.bool,
  organizationId: PropTypes.string,
  orgName: PropTypes.string,
  sitePreviewPassword: PropTypes.string,
  sitePreviewProtection: PropTypes.string,
  sitePreviewDirectory: PropTypes.string,
  productionBranch: PropTypes.string,
  siteDescription: PropTypes.string,
  location: PropTypes.object,
}

function SiteSettings({
  buildsEnabled,
  siteId,
  siteName,
  organizationId,
  orgName,
  sitePreviewPassword,
  sitePreviewProtection,
  sitePreviewDirectory,
  productionBranch,
  siteDescription,
  repository,
  location,
  sitePrefix,
}) {
  const { trackPageViewed } = useTracker()
  useEffect(() => {
    trackPageViewed(`Site Settings`)
  }, [])

  const { flags } = useFlags()

  const { data: { permissions } = {} } = useSitePermissionsFieldsFragment(
    siteId
  )

  const { sections, sidebarNavOptions } = useSiteSettingsNavigation({
    organizationId,
    siteId,
    location,
    buildsEnabled,
    flags,
  })

  return (
    <PageWithTabsContentSection>
      <InnerLayout>
        <SidebarColumn options={sidebarNavOptions} />
        <Content>
          {sections.map(({ name, active, subsections }, i) => {
            if (!active) {
              return null
            }
            let settingsSection = null
            switch (name) {
              case SiteSettingsSection.Builds:
                settingsSection = (
                  <BuildsSection
                    siteId={siteId}
                    organizationId={organizationId}
                  />
                )
                break
              case SiteSettingsSection.Preview:
                settingsSection = <PreviewSettingsSection siteId={siteId} />
                break
              case SiteSettingsSection.ExperimentalFeatures:
                settingsSection = (
                  <ExperimentalFeaturesSection siteId={siteId} />
                )
                break
              case SiteSettingsSection.Reports:
                settingsSection = (
                  <ReportsSection
                    siteId={siteId}
                    siteName={siteName}
                    buildsEnabled={buildsEnabled}
                    permissions={permissions}
                  />
                )
                break
              case SiteSettingsSection.General:
                settingsSection = (
                  <GeneralSettingsSection
                    sections={subsections}
                    organizationId={organizationId}
                    siteId={siteId}
                    siteName={siteName}
                    sitePrefix={sitePrefix}
                    productionBranch={productionBranch}
                    sitePreviewDirectory={sitePreviewDirectory}
                    sitePreviewProtection={sitePreviewProtection}
                    sitePreviewPassword={sitePreviewPassword}
                    siteDescription={siteDescription}
                    repository={repository}
                    buildsEnabled={buildsEnabled}
                    permissions={permissions}
                  />
                )
                break
              case SiteSettingsSection.Danger:
                settingsSection = (
                  <DeleteSite
                    siteId={siteId}
                    siteName={siteName}
                    permissionToDelete={permissions?.sites?.delete}
                  />
                )
                break
              case SiteSettingsSection.Integrations:
                settingsSection = (
                  <Fragment>
                    <CmsIntegrations
                      siteId={siteId}
                      orgSiteName={`${orgName}/${siteName}`}
                      permissionToEdit={permissions?.sites?.edit}
                    />

                    {buildsEnabled && (
                      <IntegrationTargetSection
                        siteId={siteId}
                        siteName={siteName}
                      />
                    )}
                  </Fragment>
                )
                break
              case SiteSettingsSection.DataSources:
                settingsSection = (
                  <CmsIntegrations
                    siteId={siteId}
                    orgSiteName={`${orgName}/${siteName}`}
                    permissionToEdit={permissions?.sites?.edit}
                  />
                )
                break
              case SiteSettingsSection.Hosting:
                settingsSection = (
                  <IntegrationTargetSection
                    siteId={siteId}
                    siteName={siteName}
                  />
                )
                break
              default:
                if (!isProduction) {
                  console.error(`Unexpected section found: ${name} - ${i}`)
                }
                settingsSection = null
            }

            return <Fragment key={name}>{settingsSection}</Fragment>
          })}
        </Content>
      </InnerLayout>
    </PageWithTabsContentSection>
  )
}

SiteSettings.propTypes = propTypes

export default SiteSettings
